import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PenLine, MapPin, Target, Radar, Send, ScrollText, Navigation,
  Unlock, CalendarClock, ArrowRight, Camera, Video, Mic, Puzzle,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const creatorSteps = [
  { icon: PenLine, title: "Name your PinQuest", desc: "Give it a title, a cover image and a story. 'Growing Up.' 'Our First Year.' 'The Great Office Hunt.'" },
  { icon: ScrollText, title: "Write your clues", desc: "A riddle, a poem, an old photograph, a cropped picture, a video of you telling half a story. Anything that points to a place." },
  { icon: Target, title: "Pin the secret target", desc: "Search the map, drop the target pin exactly where the answer lives. The hospital. The rock. That restaurant." },
  { icon: Radar, title: "Choose how close is close enough", desc: "Exact spot (50 ft)? The whole property (1,000 ft)? You set the difficulty for every clue." },
  { icon: Unlock, title: "Hide the reward", desc: "The photo they've never seen. A video of you telling the story. A message. A gift location. That's what unlocks." },
  { icon: Send, title: "Send the invite", desc: "One private link by text. No account needed to start playing. That's it — they're in the game." },
];

const playerSteps = [
  { icon: ScrollText, title: "Read the clue", desc: "Study the photo. Read the riddle twice. Argue with your sister about it." },
  { icon: MapPin, title: "Drop a pin — or go there", desc: "Solve it from your couch on the interactive map, or physically travel to the spot and tap 'Check My Location.'" },
  { icon: Radar, title: "We measure the distance", desc: "Our servers check your guess against the secret target. Too far? 'Getting warmer.' Try again — unlimited attempts." },
  { icon: Unlock, title: "UNLOCKED", desc: "Nail it and the memory opens: the photo, the video, the story behind that exact place." },
  { icon: CalendarClock, title: "Wait for the next one", desc: "The next clue might drop instantly — or Sunday at 9am. Countdown's ticking." },
];

const clueTypes = [
  { icon: ScrollText, label: "Riddles & poems" },
  { icon: Camera, label: "Photos & cropped photos" },
  { icon: Video, label: "Video stories" },
  { icon: Mic, label: "Audio clues" },
  { icon: Puzzle, label: "Mix & match blocks" },
];

const StepList = ({ steps, accent }) => (
  <div className="relative">
    <div className="absolute left-[26px] top-8 bottom-8 w-px bg-gradient-to-b from-[#2A3042] via-[#FF453A]/40 to-[#2A3042]" />
    <div className="flex flex-col gap-8">
      {steps.map((s, i) => (
        <motion.div key={s.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="flex gap-6">
          <div className={`relative shrink-0 w-[52px] h-[52px] rounded-full ${accent} border flex items-center justify-center`}>
            <s.icon size={20} className="text-[#FF453A]" />
          </div>
          <div className="pt-1">
            <p className="font-heading font-semibold text-white text-lg mb-1.5">
              <span className="text-[#FF453A] mr-2">{String(i + 1).padStart(2, "0")}</span>{s.title}
            </p>
            <p className="text-[#9BA1B0] leading-relaxed text-sm max-w-lg">{s.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

export default function HowItWorks() {
  return (
    <main data-testid="how-it-works-page" className="pt-16">
      <section className="py-20 border-b border-[#2A3042] relative grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-5">How it works</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white max-w-3xl">
            You hide the story.<br />They hunt it down.
          </h1>
          <p className="mt-6 text-lg text-[#9BA1B0] max-w-2xl leading-relaxed">
            A PinQuest is a chain of clues tied to real places. Creators build the trail;
            players crack it one location at a time.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-white mb-10 flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF453A] bg-[#FF453A]/10 border border-[#FF453A]/30 rounded-full px-4 py-1.5">Creator</span>
              Build the quest
            </h2>
            <StepList steps={creatorSteps} accent="bg-[#131620] border-[#2A3042]" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-white mb-10 flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#34C759] bg-[#34C759]/10 border border-[#34C759]/30 rounded-full px-4 py-1.5">Player</span>
              Play the quest
            </h2>
            <StepList steps={playerSteps} accent="bg-[#131620] border-[#2A3042]" />
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0b0d14] border-y border-[#2A3042]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-semibold text-3xl tracking-tight text-white mb-3">Clues can be anything</h2>
          <p className="text-[#9BA1B0] mb-10 max-w-xl">The clue system is block-based — stack text, images, video and audio into a single mystery.</p>
          <div className="flex flex-wrap gap-4">
            {clueTypes.map((c) => (
              <div key={c.label} className="flex items-center gap-3 rounded-full bg-[#131620] border border-[#2A3042] hover:border-[#FF453A] transition-colors px-6 py-3.5">
                <c.icon size={17} className="text-[#FF453A]" />
                <span className="text-sm font-medium text-[#c9cdd8]">{c.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-14 rounded-2xl bg-[#131620] border border-[#2A3042] p-8 sm:p-10 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF453A] mb-4">Example clue</p>
            <p className="text-[#c9cdd8] italic leading-relaxed font-heading text-lg">
              "Before the roads we'd someday roam,<br />
              Before you ever called somewhere home,<br />
              Find the place where your story began,<br />
              And drop your pin where you first met the old man."
            </p>
            <p className="mt-5 text-sm text-[#9BA1B0]">Target: the hospital where they were born. Radius: 500 feet.</p>
          </div>
        </div>
      </section>

      <section className="py-24 text-center">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white">
            Quests can last an afternoon.<br />Or a whole year.
          </h2>
          <p className="mt-5 text-[#9BA1B0] leading-relaxed">
            Release clues instantly, on a schedule, or every Sunday morning.
            Weekly quests turn a gift into something they look forward to for months.
          </p>
          <Link to="/early-access" data-testid="hiw-cta-btn" className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#FF453A] hover:bg-[#FF6961] transition-colors text-white font-semibold px-8 py-4 glow-red">
            Get Early Access <ArrowRight size={17} />
          </Link>
        </div>
      </section>
    </main>
  );
}
